import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Sign up function
  async function signup(email, password, displayName) {
    try {
      console.log('Starting signup process for:', email);
      const result = await createUserWithEmailAndPassword(auth, email, password);
      console.log('User created successfully:', result.user.uid);
      
      // Update profile with display name
      await updateProfile(result.user, { displayName });
      console.log('Profile updated with display name');
      
      // Create user profile in Firestore
      const userProfileData = {
        email,
        displayName,
        createdAt: new Date().toISOString(),
        onboardingCompleted: false,
        onboardingData: {},
        preferences: {
          theme: 'light',
          notifications: true,
          watchlist: []
        }
      };
      
      await createUserProfile(result.user.uid, userProfileData);
      
      // Update local state immediately
      setUserProfile(userProfileData);
      setIsAuthenticated(true);
      console.log('Local state updated with user profile');
      
      return result;
    } catch (error) {
      console.error('Error in signup:', error);
      throw error;
    }
  }

  // Sign in function
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Sign out function
  function logout() {
    return signOut(auth);
  }

  // Reset password function
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // Create user profile in Firestore
  async function createUserProfile(uid, profileData) {
    try {
      console.log('Creating user profile for:', uid, profileData);
      const userRef = doc(db, 'users', uid);
      console.log('User document reference:', userRef);
      await setDoc(userRef, profileData);
      console.log('User profile created successfully');
    } catch (error) {
      console.error('Error creating user profile:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      throw error;
    }
  }

  // Get user profile from Firestore
  async function getUserProfile(uid) {
    try {
      console.log('Fetching user profile for:', uid);
      const userRef = doc(db, 'users', uid);
      console.log('User document reference:', userRef);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const data = userDoc.data();
        console.log('User profile found:', data);
        return data;
      }
      console.log('User profile not found');
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      return null;
    }
  }

  // Update user profile
  async function updateUserProfile(uid, updates) {
    try {
      await updateDoc(doc(db, 'users', uid), updates);
      // Update local state
      if (userProfile) {
        setUserProfile({ ...userProfile, ...updates });
      }
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  // Complete onboarding
  async function completeOnboarding(uid, onboardingData) {
    try {
      console.log('Completing onboarding for user:', uid);
      console.log('Onboarding data:', onboardingData);
      
      // Validate and structure the onboarding data for Firestore
      const userData = {
        // Basic user information
        email: currentUser.email,
        displayName: currentUser.displayName,
        createdAt: new Date().toISOString(),
        onboardingCompleted: true,
        onboardingCompletedAt: new Date().toISOString(),
        lastActive: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        
        // Onboarding responses (flattened for Firestore)
        experienceLevel: onboardingData.experienceLevel || 'beginner',
        holdings: onboardingData.holdings || [],
        cashToInvest: onboardingData.cashToInvest || 0,
        currency: onboardingData.currency || 'USD',
        riskTolerance: onboardingData.riskTolerance || 'medium',
        investmentGoal: onboardingData.goal || 'long-term',
        
        // Notification preferences
        notifications: {
          priceAlerts: onboardingData.notifications?.priceAlerts ?? true,
          weeklyReports: onboardingData.notifications?.weeklyReports ?? true,
          indicatorChanges: onboardingData.notifications?.indicatorChanges ?? false
        },
        
        // User preferences
        theme: 'light',
        watchlist: [],
        
        // Portfolio information
        portfolio: {
          holdings: onboardingData.holdings || [],
          cashToInvest: onboardingData.cashToInvest || 0,
          currency: onboardingData.currency || 'USD',
          totalValue: 0,
          lastUpdated: new Date().toISOString()
        },
        
        // Trading profile
        accessLevel: 'full', // Full access after onboarding completion
        featuresEnabled: {
          aiAnalysis: true,
          technicalIndicators: true,
          portfolioTracking: true,
          priceAlerts: true,
          marketData: true,
          cryptoMarket: true
        },
        
        // Account status
        isActive: true,
        isVerified: true,
        hasCompletedOnboarding: true,
        subscriptionTier: 'free',
        lastLogin: new Date().toISOString()
      };
      
      console.log('Structured data for Firestore storage:', userData);
      
      // Update the user document in Firestore
      await updateDoc(doc(db, 'users', uid), userData);
      
      console.log('✅ User profile updated successfully in Firestore');
      
      // Update local state with the complete profile
      const updatedProfile = {
        ...userProfile,
        ...userData,
        // Ensure onboardingData is accessible for backward compatibility
        onboardingData: {
          experienceLevel: userData.experienceLevel,
          holdings: userData.holdings,
          cashToInvest: userData.cashToInvest,
          currency: userData.currency,
          riskTolerance: userData.riskTolerance,
          goal: userData.investmentGoal,
          notifications: userData.notifications
        }
      };
      
      setUserProfile(updatedProfile);
      
      // Store user profile in localStorage for personalized AI analysis
      try {
        localStorage.setItem('userProfile', JSON.stringify(updatedProfile));
        console.log('✅ User profile stored in localStorage for personalized analysis');
      } catch (error) {
        console.warn('⚠️ Could not store user profile in localStorage:', error);
      }
      
      console.log('✅ Local state updated with complete profile');
      
      console.log('✅ Onboarding completed successfully for user:', uid);
      console.log('✅ User now has full access to all platform features');
      console.log('📊 Stored data summary:');
      console.log('   • Experience Level:', userData.experienceLevel);
      console.log('   • Holdings:', userData.holdings.length, 'cryptocurrencies');
      console.log('   • Cash to Invest: $', userData.cashToInvest);
      console.log('   • Cash to Invest: $', userData.cashToInvest);
      console.log('   • Risk Tolerance:', userData.riskTolerance);
      console.log('   • Investment Goal:', userData.investmentGoal);
      console.log('   • Notifications:', Object.keys(userData.notifications).filter(k => userData.notifications[k]).length, 'enabled');
      console.log('   • Access Level:', userData.accessLevel);
      
    } catch (error) {
      console.error('❌ Error completing onboarding:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      throw error;
    }
  }

  // Refresh user profile from Firestore
  async function refreshUserProfile() {
    if (currentUser) {
      try {
        const profile = await getUserProfile(currentUser.uid);
        setUserProfile(profile);
        return profile;
      } catch (error) {
        console.error('Error refreshing user profile:', error);
        throw error;
      }
    }
  }

  // Create user profile if it doesn't exist
  async function createUserProfileIfNotExists() {
    if (currentUser && !userProfile) {
      try {
        console.log('Creating user profile if not exists for:', currentUser.uid);
        const userProfileData = {
          email: currentUser.email,
          displayName: currentUser.displayName,
          createdAt: new Date().toISOString(),
          onboardingCompleted: false,
          onboardingData: {},
          preferences: {
            theme: 'light',
            notifications: true,
            watchlist: []
          }
        };
        
        await createUserProfile(currentUser.uid, userProfileData);
        setUserProfile(userProfileData);
        return userProfileData;
      } catch (error) {
        console.error('Error creating user profile if not exists:', error);
        throw error;
      }
    }
  }

  // Test Firebase connection
  async function testFirebaseConnection() {
    try {
      console.log('Testing Firebase connection...');
      console.log('Current user:', currentUser?.uid);
      console.log('Firebase db instance:', db);
      console.log('Firebase app:', db.app);
      console.log('Firebase project ID:', db.app.options.projectId);
      
      if (currentUser) {
        const testRef = doc(db, 'users', currentUser.uid);
        console.log('Test document reference:', testRef);
        const testDoc = await getDoc(testRef);
        console.log('Test document exists:', testDoc.exists());
        
        // Test write permission
        try {
          const testData = { test: true, timestamp: new Date().toISOString() };
          await setDoc(testRef, testData, { merge: true });
          console.log('Write test successful');
          return { exists: testDoc.exists(), writeTest: true };
        } catch (writeError) {
          console.error('Write test failed:', writeError);
          console.error('Write error code:', writeError.code);
          console.error('Write error message:', writeError.message);
          return { exists: testDoc.exists(), writeTest: false, writeError };
        }
      }
      return { exists: false, writeTest: false };
    } catch (error) {
      console.error('Firebase connection test failed:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      throw error;
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          // Get user profile from Firestore
          const profile = await getUserProfile(user.uid);
          setUserProfile(profile);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setUserProfile(null);
        }
      } else {
        setUserProfile(null);
        setIsAuthenticated(false);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    logout,
    resetPassword,
    updateUserProfile,
    completeOnboarding,
    refreshUserProfile,
    createUserProfileIfNotExists,
    testFirebaseConnection,
    loading,
    isAuthenticated,
    user: currentUser,
    signUp: signup,
    signIn: login,
    signOut: logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 